// src/hooks/useColumnFilter.js - Versión actualizada
import { useState, useEffect, useCallback } from 'react';

/**
 * Hook personalizado para gestionar filtros de columna con persistencia
 * 
 * @param {Array} data - Datos a filtrar
 * @param {string} field - Campo por el cual filtrar
 * @param {Function} valueGetter - Función opcional para obtener el valor (útil para campos anidados)
 * @param {string} moduleKey - Identificador único del módulo (ej: 'works', 'people')
 * @returns {Object} - Estado y funciones para manejar filtros
 */
export function useColumnFilter(data, field, valueGetter, moduleKey = '') {
  // Generar una clave única para este filtro en sessionStorage
  const filterKey = `${moduleKey}-${field}`.toLowerCase();
  const storageKey = `filter-${filterKey}`;
  
  // Función para obtener los filtros guardados
  const getSavedFilters = useCallback(() => {
    try {
      const savedFilters = sessionStorage.getItem(storageKey);
      return savedFilters ? JSON.parse(savedFilters) : [];
    } catch (error) {
      console.error('Error al recuperar filtros guardados:', error);
      return [];
    }
  }, [storageKey]);
  
  // Estados para las opciones de filtro y filtros activos
  // Inicializar con los filtros guardados en sessionStorage si existen
  const [filterOptions, setFilterOptions] = useState([]);
  const [activeFilters, setActiveFilters] = useState(getSavedFilters());
  
  // Extraer valores únicos para las opciones de filtro y restaurar estado de checked
  useEffect(() => {
    if (!data || data.length === 0) return;
    
    const savedFilters = getSavedFilters();
    
    // Set para guardar valores únicos
    const uniqueValues = new Set();
    
    // Recorrer los datos y extraer valores únicos
    data.forEach(item => {
      let value;
      
      // Si hay una función valueGetter, usarla para obtener el valor
      if (valueGetter) {
        value = valueGetter(item);
      } else {
        // Sino, intentar obtener el valor directamente
        value = item[field];
      }
      
      // Si el valor es válido y no está ya en el conjunto, agregarlo
      if (value !== undefined && value !== null && value !== '') {
        uniqueValues.add(value);
      }
    });
    
    // Convertir los valores únicos a opciones de filtro
    const options = Array.from(uniqueValues).map(value => {
      // Buscar si el valor ya existe en las opciones actuales
      const existingOption = filterOptions.find(opt => opt.value === value);
      
      // Si existe, mantener su estado checked
      if (existingOption) {
        return existingOption;
      }
      
      // Verificar si este valor estaba guardado como activo en sessionStorage
      const isActive = savedFilters.some(filter => filter.value === value);
      
      // Crear una nueva opción con ID único basado en filterKey
      return {
        id: `${filterKey}-${value}`, // ← ID único usando filterKey
        label: value,
        value: value,
        checked: isActive,
        filterKey: filterKey // ← Agregar filterKey para identificación
      };
    });
    
    // Ordenar opciones alfabéticamente
    options.sort((a, b) => {
      // Intentar ordenar como números si ambos son numéricos
      if (!isNaN(a.label) && !isNaN(b.label)) {
        return Number(a.label) - Number(b.label);
      }
      // Si no, ordenar alfabéticamente
      return String(a.label).localeCompare(String(b.label));
    });
    
    // Actualizar los filtros activos solo si es la primera carga
    if (savedFilters.length > 0 && activeFilters.length === 0) {
      // Asegurar que los filtros guardados tengan el format correcto
      const formattedSavedFilters = savedFilters.map(filter => ({
        ...filter,
        id: filter.id || `${filterKey}-${filter.value}`, // Asegurar ID único
        filterKey: filterKey
      }));
      setActiveFilters(formattedSavedFilters);
    }
    
    // Actualizar las opciones de filtro SOLO si hay cambios
    if (options.length !== filterOptions.length || 
        !options.every(opt => filterOptions.some(fo => fo.value === opt.value))) {
      setFilterOptions(options);
    }
  }, [data, field, valueGetter, filterOptions, getSavedFilters, activeFilters.length, filterKey]);

  // Función para actualizar las opciones MEMORIZADA
  const handleOptionsUpdate = useCallback((newOptions) => {
    setFilterOptions(newOptions);
    
    // Actualizar los filtros activos asegurando formato correcto
    const newActiveFilters = newOptions
      .filter(opt => opt.checked)
      .map(opt => ({
        ...opt,
        filterKey: filterKey // Asegurar que tenga filterKey
      }));
    
    setActiveFilters(newActiveFilters);
    
    // Guardar en sessionStorage
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(newActiveFilters));
    } catch (error) {
      console.error('Error al guardar filtros:', error);
    }
  }, [storageKey, filterKey]);

  // Función para actualizar los filtros activos MEMORIZADA
  const handleFiltersChange = useCallback((newFilters) => {
    // Asegurar que todos los filtros tengan filterKey
    const formattedFilters = newFilters.map(filter => ({
      ...filter,
      filterKey: filterKey
    }));
    
    setActiveFilters(formattedFilters);
    
    // Guardar en sessionStorage
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(formattedFilters));
    } catch (error) {
      console.error('Error al guardar filtros:', error);
    }
  }, [storageKey, filterKey]);

  // Función para limpiar los filtros guardados
  const clearSavedFilters = useCallback(() => {
    setActiveFilters([]);
    
    // Actualizar opciones para desmarcar todos
    setFilterOptions(prev => prev.map(opt => ({ ...opt, checked: false })));
    
    // Eliminar del sessionStorage
    try {
      sessionStorage.removeItem(storageKey);
    } catch (error) {
      console.error('Error al limpiar filtros guardados:', error);
    }
  }, [storageKey]);

  // Función para filtrar los datos según los filtros activos MEMORIZADA
  const filterData = useCallback((itemsToFilter) => {
    // Si no hay filtros activos, devolver todos los datos
    if (!activeFilters || activeFilters.length === 0) {
      return itemsToFilter;
    }
    
    // Filtrar los datos según los filtros activos
    return itemsToFilter.filter(item => {
      let itemValue;
      
      // Obtener el valor del item según corresponda
      if (valueGetter) {
        itemValue = valueGetter(item);
      } else {
        itemValue = item[field];
      }
      
      // Verificar si el valor del item coincide con algún filtro activo
      return activeFilters.some(filter => filter.value === itemValue);
    });
  }, [activeFilters, field, valueGetter]);

  // Devolver las funciones y estados necesarios
  return {
    filterOptions,
    activeFilters,
    handleOptionsUpdate,
    handleFiltersChange,
    filterData,
    clearSavedFilters,
    filterKey // ← NUEVO: devolver filterKey para usar en FilterSelect
  };
}

export default useColumnFilter;